import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { DataRole, DateTime } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { DomainResult } from '@mode-switch/query';
import {
  DatePart,
  EncRelativeDateFilter,
  EncRelativeDateFilterField,
  Encoding,
  RelativeDateFilterHelpers,
} from '@mode-vegas/vegas';
import { IconComponent } from '@mode/capra';
import { ChartFacade } from '@mode/charts/contract';
import { FlamingoResponse, FlamingoTypes } from '@mode/shared/contract-common';
import { LabeledMenuComponent, LoaderComponent } from '@mode/shared/ui';
import { formatInUTC, isPresent } from '@mode/shared/util-js';
import { CompiledField, FormulaOperations } from '@mode/shared/util-switch';
import { escape } from 'lodash';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  debounce,
  distinctUntilChanged,
  filter,
  first,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { idFromFormula } from '../filter-modal/filter-modal-helpers';
import { findOrFirst } from '../helpers/field-helpers';
import {
  Period,
  PeriodOption,
  WeekStartOption,
  YearStartOption,
  getDatePart,
  getPartialFilterConfigFromPeriod,
  getPeriod,
  getPeriodOptions,
  relativeDateFilterSourcedByField,
  weekStartOptions,
  yearStartOptions,
} from './helpers';
import { RelativeDateFromToComponent } from './relative-date-fromto.component';

const unsetType: EncRelativeDateFilter.FromTo = { type: 'unset' };

@Component({
  standalone: true,
  imports: [CommonModule, IconComponent, LabeledMenuComponent, LoaderComponent, RelativeDateFromToComponent],
  selector: 'mode-relative-date-panel',
  templateUrl: './relative-date-panel.component.html',
  styleUrls: ['./relative-date-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelativeDatePanelComponent implements OnChanges, OnDestroy, OnInit {
  @Input() view!: FlamingoTypes.ModeView;
  @Input() encoding!: Encoding;
  @Input() queryRunToken!: string;
  @Input() field!: CompiledField;
  @Input() filter!: EncRelativeDateFilterField;

  @Output() filterChange = new EventEmitter<EncRelativeDateFilterField>();
  @Output() filterReplace = new EventEmitter<EncRelativeDateFilterField>();
  @Output() isInvalid = new EventEmitter<boolean>();

  dataMinTimestamp: number | undefined;
  dataMaxTimestamp: number | undefined;

  showWeekStarts = false;
  weekStartOptions = weekStartOptions;
  selectedWeekStart = weekStartOptions[0];

  showYearStarts = false;
  yearStartOptions = yearStartOptions;
  selectedYearStart = yearStartOptions[0];

  periodOptions = getPeriodOptions(undefined, this.selectedWeekStart.value, this.selectedYearStart.value);
  selectedPeriod? = this.periodOptions[1];

  from: EncRelativeDateFilter.FromTo | undefined = unsetType;
  to: EncRelativeDateFilter.FromTo | undefined = unsetType;

  isValid = true;
  filterDescription = '';

  private destroyed$ = new Subject<void>();
  isLoadingSubject$ = new BehaviorSubject<boolean>(true);
  showLoader$: Observable<boolean>;

  private domainAliasSubject$ = new BehaviorSubject<string | undefined>(undefined);
  private domainAlias$: Observable<string>;

  private domainResultRefId = 'filter-date-range';
  private domainResult$: Observable<DomainResult.Continuous<DataRole>>;

  constructor(private chartFacade: ChartFacade) {
    this.showLoader$ = this.isLoadingSubject$.pipe(
      debounce((showLoader) => {
        return showLoader ? timer(350) : EMPTY;
      })
    );

    this.domainAlias$ = this.domainAliasSubject$.pipe(filter(isPresent), distinctUntilChanged());
    this.domainResult$ = this.domainAlias$.pipe(
      switchMap((domainAlias) =>
        this.chartFacade
          .createSelectDomainValues(this.domainResultRefId, domainAlias)
          .pipe(
            filter(
              (domainResult): domainResult is DomainResult.Continuous<DataRole> =>
                domainResult?.type === Attr.Type.Continuous
            )
          )
      )
    );
  }

  ngOnInit(): void {
    // Handle any query errors
    this.chartFacade
      .createErrorFromDomainQuery(this.domainResultRefId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((error) => this.onLoadError(error));

    // Fetch all values when the formula changes
    let req = 0;
    this.domainAlias$.subscribe((domainAlias) => {
      this.fetchFilterDomain(domainAlias, ++req === 1 /* loader on first fetch */);
    });

    // Set the domain min and max
    this.domainResult$.pipe(takeUntil(this.destroyed$)).subscribe((result) => {
      this.dataMinTimestamp = (result.min as number) ?? undefined;
      this.dataMaxTimestamp = (result.max as number) ?? undefined;

      // Notify that loading is complete
      this.isLoadingSubject$.next(false);
    });

    // 1) Disable Apply & Close on init until we can 2) fill in the blanks
    const initialFilterIsEmptyFixedDate = isEmptyFixedDateFilter(this.filter);
    if (initialFilterIsEmptyFixedDate) {
      // #1
      setTimeout(() => {
        this.isInvalid.emit(true);
      });

      // #2
      this.domainResult$.pipe(takeUntil(this.destroyed$), first()).subscribe(() =>
        this.onChangePeriod(
          findOrFirst(this.periodOptions, (value) => {
            return value === Period.Fixed;
          })
        )
      );
    }
  }

  private fetchFilterDomain(domainAlias: string, loading = false): void {
    this.isLoadingSubject$.next(loading);
    this.chartFacade.fetchFilterDomainValues(
      this.queryRunToken,
      this.view,
      this.encoding,
      this.field,
      this.domainResultRefId,
      domainAlias,
      false
    );
  }

  ngOnChanges(): void {
    this.domainAliasSubject$.next(
      idFromFormula(
        /*
         * Hashing on displayName will prevent us from making superfluous domain
         * requests when a week/year start param changes but doesn't apply to the
         * current DateTime.Unit, eg:
         *
         * DATETRUNC('DAY', date, Mon, Aug) and DATETRUNC('DAY', date, Sun, Apr)
         * produce the same data as the week/year start does not apply to DAY;
         * the displayName in both cases will be DAY(date)
         */
        this.field.displayName
      )
    );

    this.from = this.filter.from;
    this.to = this.filter.to;

    this.sync();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  sync(): void {
    const weekStart = DateTime.weekStartDow(
      // Formula with week start encoded in it
      this.field.weekStart ??
        // No week start config
        this.field.defaultWeekStart
    );

    this.selectedWeekStart = findOrFirst(weekStartOptions, (value) => {
      return value === weekStart;
    });

    const yearStart = DateTime.yearStartMonth(
      // Formula with year start encoded in it
      this.field.yearStart ??
        // No year start config
        this.field.defaultYearStart
    );

    this.selectedYearStart = findOrFirst(yearStartOptions, (value) => {
      return value === yearStart;
    });

    const period = getPeriod(this.filter);
    this.periodOptions = getPeriodOptions(undefined, this.selectedWeekStart.value, this.selectedYearStart.value);
    this.selectedPeriod = findOrFirst(this.periodOptions, (value) => {
      return value === period;
    });

    const parts = [getDatePart(this.from), getDatePart(this.to)];
    this.showWeekStarts = parts.some((part) => part === DatePart.WEEK);
    this.showYearStarts = parts.some((part) => part === DatePart.QUARTER || part === DatePart.YEAR);

    this.validate();
    this.setDescription();
  }

  onChangePeriod(period: PeriodOption): void {
    this.selectedPeriod = period;

    const { from, to } = getPartialFilterConfigFromPeriod(period.value);
    this.from = from;
    this.to = to;

    if (period.value === Period.Fixed) {
      const minOrMax = this.dataMinTimestamp ?? this.dataMaxTimestamp;

      // If we have a min then we have a max (and vice versa)
      if (typeof minOrMax === 'number') {
        const minValue = new Date(this.dataMinTimestamp as number).setUTCHours(0, 0, 0, 0);
        const maxValue = new Date(this.dataMaxTimestamp as number).setUTCHours(23, 59, 59, 999);

        this.from = {
          type: 'fixed',
          value: minValue,
        };

        this.to = {
          type: 'fixed',
          value: maxValue,
        };
      }
      // No min/max
      else {
        this.from = unsetType;
        this.to = unsetType;
      }
    }

    this.onChange();
  }

  onChangeFrom(from: EncRelativeDateFilter.FromTo | undefined): void {
    this.from = from;

    // Custom if valid
    if (from !== undefined) {
      this.selectedPeriod = this.periodOptions[0];
    }

    // Set UTC start of day
    if (from?.type === 'fixed') {
      const value = new Date(from.value).setUTCHours(0, 0, 0, 0);
      this.from = { ...from, value };
    }

    this.onChange();
  }

  onChangeTo(to: EncRelativeDateFilter.FromTo | undefined): void {
    this.to = to;

    // Custom if valid
    if (to !== undefined) {
      this.selectedPeriod = this.periodOptions[0];
    }

    // Set UTC end of day
    if (to?.type === 'fixed') {
      const value = new Date(to.value).setUTCHours(23, 59, 59, 999);
      this.to = { ...to, value };
    }

    this.onChange();
  }

  onChangeWeekStart(start: WeekStartOption): void {
    this.selectedWeekStart = start;

    // Persist the selection in the field formula
    this._encodeWYStartsIntoFieldFormula(true /*year is optional*/);

    this.onChange();
  }

  onChangeYearStart(start: YearStartOption): void {
    this.selectedYearStart = start;

    // Persist the selection in the field formula
    this._encodeWYStartsIntoFieldFormula();

    this.onChange();
  }

  _encodeWYStartsIntoFieldFormula(yOptional = false): void {
    const weekStart = DateTime.weekStart(this.selectedWeekStart.value);
    const yearStart = DateTime.yearStart(this.selectedYearStart.value);
    this.field = this.field.withNewFormula(
      FormulaOperations.dateTrunc(
        this.field,
        DateTime.Unit.Day,
        weekStart,
        yOptional
          ? // Omit year if we're just setting the week start; FormulaOp
            // carries over year starts defined in the formula
            undefined
          : yearStart
      )
    );
  }

  onChange(): void {
    this.validate();
    this.setDescription();

    const prevFilter = this.filter;
    const nextFilter = this.makeFilter();

    if (nextFilter) {
      this.filter = nextFilter;

      const updateFn =
        isEmptyFixedDateFilter(prevFilter) && !isEmptyFixedDateFilter(nextFilter)
          ? this.filterReplace
          : this.filterChange;

      updateFn.emit(nextFilter);
    }
  }

  onLoadError(error: FlamingoResponse.FlamingoErrorResponse | undefined) {
    if (error !== undefined) {
      // Clear the loading indicator, but don't set an error state.
      // This will only affect the default values for a fixed range filter.
      // It is still possible to manually set dates for a fixed range filter
      // or create a relative date filter.
      this.isLoadingSubject$.next(false);
      console.warn(`Unable to load filter data range. Error: ${JSON.stringify(error)}`);
    }
  }

  get isFixedRange() {
    return this.selectedPeriod?.value === Period.Fixed;
  }

  private validate(filter = this.makeFilter()) {
    this.isValid = RelativeDateFilterHelpers.isValidFilter(filter);
    this.isInvalid.emit(!this.isValid);
  }

  private setDescription(filter = this.makeFilter()) {
    this.filterDescription = getFilterDescription(
      this.field,
      filter
        ? {
            ...filter,
            weekStart: filter.weekStart ?? DateTime.weekStartDow(this.selectedWeekStart.value),
            yearStart: filter.yearStart ?? DateTime.yearStartMonth(this.selectedYearStart.value),
          }
        : undefined,
      this.selectedPeriod?.value
    );
  }

  private makeFilter(): EncRelativeDateFilterField | undefined {
    if (this.from && this.to) {
      // Normalize RelativeDate filters such that the filter w/y starts are in sync with
      // the filter formula, _falling back_ to the filter w/y starts when present
      return relativeDateFilterSourcedByField(
        {
          ...this.filter,
          from: this.from,
          to: this.to,
        },
        this.field
      );
    }
  }
}

function getFilterTypeDescription(filter: EncRelativeDateFilter): string {
  if (isRelative(filter.from) || isRelative(filter.to)) {
    return 'Relative filter';
  }
  return 'Fixed range filter';
}

function getFilterDescription(
  field: CompiledField,
  filter: EncRelativeDateFilter | undefined,
  period: Period | undefined
): string {
  if (!RelativeDateFilterHelpers.isValidFilter(filter)) {
    // Has a from->to
    if (filter !== undefined) {
      let range: ReturnType<typeof RelativeDateFilterHelpers.getDateRange> | undefined;
      try {
        range = RelativeDateFilterHelpers.getDateRange(filter);
      } catch {
        // handled below
      }

      // Invalid.. from is after to
      if (range && +(range.from ?? NaN) > +(range.to ?? NaN)) {
        return 'Invalid filter date range. The start date is after the end date.';
      }
    }
    // Invalid fixed
    else if (period === Period.Fixed) {
      return 'Fixed date ranges must have a start and end date.';
    }

    return 'Invalid filter date range.';
  }

  const range = RelativeDateFilterHelpers.getDateRange(filter);

  let fieldDisplayName = escape(field.displayName);
  if (field.dateFuncUnit === DateTime.Unit.Day) {
    fieldDisplayName = escape(field.fieldName);
  }

  // Unbounded
  if (!(range.from || range.to)) {
    return `
      ${getFilterTypeDescription(filter)}:<br>
      This filter will display all available <strong>${fieldDisplayName}</strong> data.
    `;
  }
  // Up to
  else if (!range.from && range.to) {
    return `
      ${getFilterTypeDescription(filter)}:<br>
      This filter will display all available <strong>${fieldDisplayName}</strong> data up to
      ${formatInUTC(range.to, "MMM d',' yyyy h:mmaaa' UTC'")}.
    `;
  }
  // Onward
  else if (range.from && !range.to) {
    return `
      ${getFilterTypeDescription(filter)}:<br>
      This filter will display all available <strong>${fieldDisplayName}</strong> data from
      ${formatInUTC(range.from, "MMM d',' yyyy h:mmaaa' UTC'")} onward.
    `;
  }
  // Both dates present (explicit check for TS)
  else if (range.from && range.to) {
    return `
      ${getFilterTypeDescription(filter)}:<br>
      This filter will display all available <strong>${fieldDisplayName}</strong> data from
      ${formatInUTC(range.from, "MMM d',' yyyy h:mmaaa' UTC'")} to ${formatInUTC(
        range.to,
        "MMM d',' yyyy h:mmaaa' UTC'"
      )}.
    `;
  }

  // Shouldn't be possible
  return 'Invalid filter date range.';
}

function isRelative(fromto?: EncRelativeDateFilter.FromTo): fromto is EncRelativeDateFilter.FromTo.Relative {
  return fromto?.type === 'relative';
}

function isEmptyFixedDateFilter(filter: EncRelativeDateFilter): boolean {
  return (
    filter.from.type === 'fixed' &&
    Number.isNaN(filter.from.value) &&
    filter.to.type === 'fixed' &&
    Number.isNaN(filter.to.value)
  );
}
